import { useQuery } from "@tanstack/react-query";
import {dealerApi} from "../lib/api";
import { useDealerSession } from "../lib/session";
import { tzs } from "../lib/format";
import { PageHead } from "../components/PageHead";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface WalletBalances {
  available_minor: number;
  reserved_minor: number;
  commission_minor: number;
}

export function Settings() {
  const { dealer, signOut } = useDealerSession();
  const navigate = useNavigate();
  const [confirmLogout, setConfirmLogout] = useState(false);

  const wallet = useQuery({
    queryKey: ["dealer-wallet"],
    queryFn: () =>
      dealerApi.get<WalletBalances>("/superdealer/v1/wallet"),
  });

  return (
    <div>
      <PageHead title="Settings" description="Dealership profile and wallet overview." />

      <div className="dealer-settings-grid">
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Dealership profile</h3>
          </div>
          <div className="admin-detail-grid">
            <div className="admin-stat-row">
              <span className="ev-body-muted">Dealer code</span>
              <strong className="dash-mono">{dealer?.code}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted">Business name</span>
              <strong>{dealer?.business_name}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted">Owner</span>
              <strong>{dealer?.owner_name ?? "—"}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted">Phone</span>
              <strong>{dealer?.phone}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted">Region</span>
              <strong>{dealer?.region ?? "—"}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted">Status</span>
              <span className="dash-pill dash-pill-success">{dealer?.state}</span>
            </div>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Wallet & limits</h3>
          </div>
          <div className="admin-detail-grid">
            <div className="admin-stat-row">
              <span className="ev-body-muted">Available float</span>
              <strong>{tzs(wallet.data?.available_minor)}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted">Reserved</span>
              <strong>{tzs(wallet.data?.reserved_minor)}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted">Commission earned</span>
              <strong>{tzs(wallet.data?.commission_minor)}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted">Per-transfer limit</span>
              <strong>{tzs(dealer?.txn_transfer_limit_minor)}</strong>
            </div>
            <div className="admin-stat-row">
              <span className="ev-body-muted">Daily transfer limit</span>
              <strong>{tzs(dealer?.daily_transfer_limit_minor)}</strong>
            </div>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Account</h3>
          </div>
          <p className="ev-body-muted" style={{ marginBottom: 12 }}>
            Sign out of this device, or contact your Evocha account manager to change your PIN,
            raise limits, or resolve a transfer.
          </p>
          <button className="ev-btn ev-btn-outline" onClick={() => setConfirmLogout(true)}>
            Sign out
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        onConfirm={() => {
          signOut();
          navigate("/login");
        }}
        title="Sign out?"
        message="You will need your phone number and PIN to sign back in."
        confirmLabel="Sign out"
        cancelLabel="Cancel"
      />
    </div>
  );
}
